/**
 * HorariosEditor — Editor de reglas horarias recurrentes de una sala.
 *
 * Rediseño: layout de lista por día (filas completas) en vez de grilla de 7
 * columnas. Cada día muestra sus bloques en tarjetas amplias con hora,
 * vigencia y acciones (editar / eliminar). Soporta múltiples bloques por
 * día con diferente vigencia.
 *
 * Datos: subcolección `salas/{salaId}/horarios`.
 */
import { useState, useEffect, useMemo } from 'react';
import { collection, onSnapshot, Timestamp } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import {
  Plus,
  Trash2,
  Clock,
  Pencil,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  CalendarRange,
  CalendarDays,
  CalendarOff,
  CalendarClock,
  Lock,
  List,
} from 'lucide-react';
import { db, functions } from '../../config/firebase';
import { Input } from '../ui/Input';
import { useToast } from '../../contexts/ToastContext';
import { useTheme } from '../../hooks/useTheme';
import styles from './HorariosEditor.module.css';

const DIAS = [
  'Domingo',
  'Lunes',
  'Martes',
  'Miércoles',
  'Jueves',
  'Viernes',
  'Sábado',
];

// Orden de columnas en la vista semanal (Lunes → Domingo, estilo Outlook).
const ORDEN_SEMANA = [1, 2, 3, 4, 5, 6, 0];
const HORA_PX = 34; // alto de cada fila de hora en la grilla

interface PaletaTurno { fill: string; border: string; text: string }
interface Turno {
  /** Color sólido medio para el punto de la leyenda (visible en claro y oscuro). */
  accent: string;
  light: PaletaTurno;
  /** Modo oscuro: fill translúcido + texto claro, para verse tenue. */
  dark: PaletaTurno;
  label: string;
}
const T_MANANA: Turno = {
  accent: '#1D9E75',
  light: { fill: '#E1F5EE', border: '#0F6E56', text: '#085041' },
  dark: { fill: 'rgba(29,158,117,0.16)', border: 'rgba(29,158,117,0.5)', text: '#9FE1CB' },
  label: 'Mañana',
};
const T_TARDE: Turno = {
  accent: '#378ADD',
  light: { fill: '#E6F1FB', border: '#185FA5', text: '#0C447C' },
  dark: { fill: 'rgba(55,138,221,0.16)', border: 'rgba(55,138,221,0.5)', text: '#B5D4F4' },
  label: 'Tarde',
};
const T_NOCHE: Turno = {
  accent: '#7F77DD',
  light: { fill: '#EEEDFE', border: '#534AB7', text: '#3C3489' },
  dark: { fill: 'rgba(127,119,221,0.18)', border: 'rgba(127,119,221,0.5)', text: '#CECBF6' },
  label: 'Noche',
};
const T_FINDE: Turno = {
  accent: '#EF9F27',
  light: { fill: '#FAEEDA', border: '#BA7517', text: '#854F0B' },
  dark: { fill: 'rgba(239,159,39,0.16)', border: 'rgba(239,159,39,0.5)', text: '#FAC775' },
  label: 'Fin de semana',
};

// Paleta gris para reglas aún NO vigentes (programadas para el futuro).
const GRIS_LIGHT: PaletaTurno = { fill: '#eef1f4', border: '#94a3b8', text: '#64748b' };
const GRIS_DARK: PaletaTurno = { fill: 'rgba(148,163,184,0.14)', border: 'rgba(148,163,184,0.5)', text: '#cbd5e1' };

/** Formatea "YYYY-MM-DD" → "12 de junio de 2026". */
function fmtFechaLarga(iso: string): string {
  const d = new Date(`${iso}T12:00:00`);
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('es', { day: 'numeric', month: 'long', year: 'numeric' });
}

function hhToMin(hhmm: string): number {
  const [h, m] = hhmm.split(':').map(Number);
  return (h || 0) * 60 + (m || 0);
}

/** Color del bloque según turno: fin de semana → ámbar; entre semana por hora
 *  de inicio (mañana <12h, tarde 12–18h, noche ≥18h). */
function turnoDe(dia: number, horaInicio: string): Turno {
  if (dia === 0 || dia === 6) return T_FINDE;
  const h = Math.floor(hhToMin(horaInicio) / 60);
  if (h < 12) return T_MANANA;
  if (h < 18) return T_TARDE;
  return T_NOCHE;
}

// ── Utilidades de fecha para la vista semanal con fechas reales ──
function addDays(d: Date, n: number): Date {
  const r = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  r.setDate(r.getDate() + n);
  return r;
}
/** Lunes de la semana de `base`, desplazada `offsetWeeks` semanas. */
function startOfWeekMonday(base: Date, offsetWeeks: number): Date {
  const d = new Date(base.getFullYear(), base.getMonth(), base.getDate());
  const day = d.getDay(); // 0=Dom … 6=Sáb
  const diffToMon = day === 0 ? -6 : 1 - day;
  d.setDate(d.getDate() + diffToMon + offsetWeeks * 7);
  return d;
}
function toDateStr(d: Date): string {
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${m}-${day}`;
}

// ── Suspensiones puntuales (subcolección salas/{id}/suspensiones) ──
type TipoSuspension = 'dia_completo' | 'rango_horas' | 'multi_dia';
interface Suspension {
  id: string;
  tipo: TipoSuspension;
  fecha?: string;
  horaInicio?: string;
  horaFin?: string;
  fechaDesde?: string;
  fechaHasta?: string;
  motivo: string;
}

/** Devuelve las suspensiones que afectan una fecha (YYYY-MM-DD), separadas en
 *  las de día completo (cubren toda la columna) y las de rango horario. */
function suspensionesDeFecha(dateStr: string, all: Suspension[]): {
  fullDay: Suspension[];
  rangos: Suspension[];
} {
  const fullDay: Suspension[] = [];
  const rangos: Suspension[] = [];
  for (const s of all) {
    if (s.tipo === 'dia_completo' && s.fecha === dateStr) fullDay.push(s);
    else if (
      s.tipo === 'multi_dia' && s.fechaDesde && s.fechaHasta &&
      dateStr >= s.fechaDesde && dateStr <= s.fechaHasta
    ) fullDay.push(s);
    else if (s.tipo === 'rango_horas' && s.fecha === dateStr && s.horaInicio && s.horaFin) {
      rangos.push(s);
    }
  }
  return { fullDay, rangos };
}

// ── Bloqueos recurrentes (subcolección salas/{id}/bloqueos) ──
interface Bloqueo {
  id: string;
  diasSemana: number[];
  horaInicio: string;
  horaFin: string;
  motivo: string;
  vigenciaDesde: Timestamp;
  vigenciaHasta: Timestamp | null;
}

function tsToDateStr(ts: Timestamp | null | undefined): string | null {
  if (!ts) return null;
  try { return ts.toDate().toISOString().slice(0, 10); } catch { return null; }
}

/** Bloqueos recurrentes que aplican a un día/fecha: el día está en diasSemana
 *  y la fecha cae dentro de la vigencia. */
function bloqueosDeFecha(dia: number, dateStr: string, all: Bloqueo[]): Bloqueo[] {
  return all.filter((b) => {
    if (!Array.isArray(b.diasSemana) || !b.diasSemana.includes(dia)) return false;
    const desde = tsToDateStr(b.vigenciaDesde);
    const hasta = tsToDateStr(b.vigenciaHasta);
    if (desde && dateStr < desde) return false;
    if (hasta && dateStr > hasta) return false;
    return true;
  });
}

interface ReglaHorario {
  id: string;
  diaSemana: number;
  horaInicio: string;
  horaFin: string;
  vigenciaDesde: Timestamp;
  vigenciaHasta: Timestamp | null;
  prioridad?: number;
}

interface FormState {
  horaInicio: string;
  horaFin: string;
  vigenciaDesde: string;
  vigenciaHasta: string;
}

const EMPTY_FORM: FormState = {
  horaInicio: '08:00',
  horaFin: '12:00',
  vigenciaDesde: new Date().toISOString().slice(0, 10),
  vigenciaHasta: '',
};

const callCrearRegla = httpsCallable(functions, 'portal_crearReglaHorario');
const callActualizarRegla = httpsCallable(functions, 'portal_actualizarReglaHorario');
const callEliminarRegla = httpsCallable(functions, 'portal_eliminarReglaHorario');
// CFs para el horario PERSONALIZADO del médico en la sala (sin CF de update →
// editar = borrar + recrear).
const callCrearHorarioMedico = httpsCallable(functions, 'portal_crearHorarioMedicoSala');
const callEliminarHorarioMedico = httpsCallable(functions, 'portal_eliminarHorarioMedicoSala');

export interface HorariosEditorProps {
  salaId: string;
  /**
   * Si se provee, el editor gestiona el horario PERSONALIZADO de este médico en
   * la sala (subcolección `salas/{salaId}/medicos_sala/{medicoId}/horarios`) en
   * vez del horario general de la sala. En este modo no se muestran suspensiones
   * ni bloqueos (que son a nivel de sala).
   */
  medicoId?: string;
}

export function HorariosEditor({ salaId, medicoId }: HorariosEditorProps) {
  const { addToast } = useToast();
  const { isDark } = useTheme();
  const esMedico = !!medicoId;
  // Ruta de la subcolección de reglas según el modo.
  const horariosPath = esMedico
    ? `salas/${salaId}/medicos_sala/${medicoId}/horarios`
    : `salas/${salaId}/horarios`;
  const [reglas, setReglas] = useState<ReglaHorario[]>([]);
  const [suspensiones, setSuspensiones] = useState<Suspension[]>([]);
  const [bloqueos, setBloqueos] = useState<Bloqueo[]>([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState<'semana' | 'lista'>('semana');
  const [weekOffset, setWeekOffset] = useState(0); // 0 = semana actual
  const [expandedDays, setExpandedDays] = useState<Set<number>>(new Set([1, 2, 3, 4, 5]));

  // Modal state — null = closed, { dia, regla? } = open
  const [modal, setModal] = useState<{
    dia: number;
    regla?: ReglaHorario; // present when editing
  } | null>(null);
  const [form, setForm] = useState<FormState>({ ...EMPTY_FORM });
  const [submitting, setSubmitting] = useState(false);
  const [deleting, setDeleting] = useState<string | null>(null);

  // ── Suscripción real-time ──
  useEffect(() => {
    if (!salaId) return;
    const ref = collection(db, horariosPath);
    const unsub = onSnapshot(
      ref,
      (snap) => {
        const items: ReglaHorario[] = snap.docs.map((d) => ({
          id: d.id,
          ...(d.data() as Omit<ReglaHorario, 'id'>),
        }));
        setReglas(items);
        setLoading(false);
      },
      (err) => {
        console.error('[HorariosEditor]', err);
        addToast('error', 'No se pudieron cargar los horarios');
        setLoading(false);
      },
    );
    return () => unsub();
  }, [salaId, horariosPath, addToast]);

  // ── Suscripción a suspensiones (solo a nivel de sala, no por médico) ──
  useEffect(() => {
    if (!salaId || esMedico) return;
    const ref = collection(db, `salas/${salaId}/suspensiones`);
    const unsub = onSnapshot(
      ref,
      (snap) => {
        setSuspensiones(
          snap.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<Suspension, 'id'>) })),
        );
      },
      () => { /* sin permisos / error: simplemente no mostramos suspensiones */ },
    );
    return () => unsub();
  }, [salaId, esMedico]);

  // ── Suscripción a bloqueos recurrentes (solo a nivel de sala, no por médico) ──
  useEffect(() => {
    if (!salaId || esMedico) return;
    const ref = collection(db, `salas/${salaId}/bloqueos`);
    const unsub = onSnapshot(
      ref,
      (snap) => {
        setBloqueos(
          snap.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<Bloqueo, 'id'>) })),
        );
      },
      () => { /* sin permisos / error: no mostramos bloqueos */ },
    );
    return () => unsub();
  }, [salaId, esMedico]);

  // ── Reglas agrupadas por día ──
  const reglasPorDia = useMemo(() => {
    const map: Record<number, ReglaHorario[]> = {};
    for (let i = 0; i < 7; i++) map[i] = [];
    for (const r of reglas) {
      map[r.diaSemana]?.push(r);
    }
    for (const dia of Object.keys(map)) {
      map[Number(dia)].sort((a, b) => a.horaInicio.localeCompare(b.horaInicio));
    }
    return map;
  }, [reglas]);

  // ── Toggle día expandido ──
  const toggleDay = (dia: number) => {
    setExpandedDays((prev) => {
      const next = new Set(prev);
      if (next.has(dia)) next.delete(dia);
      else next.add(dia);
      return next;
    });
  };

  // ── Rango de horas visible en la vista semanal: al menos 07–19, se expande
  //    para abarcar los bloques más tempranos/tardíos configurados. ──
  const { startHour, endHour } = useMemo(() => {
    let min = 24, max = 0;
    for (const r of reglas) {
      const s = Math.floor(hhToMin(r.horaInicio) / 60);
      const e = Math.ceil(hhToMin(r.horaFin) / 60);
      if (s < min) min = s;
      if (e > max) max = e;
    }
    if (min === 24) { min = 7; max = 19; }
    return { startHour: Math.max(0, Math.min(min, 7)), endHour: Math.min(24, Math.max(max, 19)) };
  }, [reglas]);

  // ── Fechas reales de la semana mostrada (Lun→Dom según ORDEN_SEMANA) ──
  const hoyStr = useMemo(() => toDateStr(new Date()), []);
  const semana = useMemo(() => {
    const monday = startOfWeekMonday(new Date(), weekOffset);
    return ORDEN_SEMANA.map((dia, i) => {
      const date = addDays(monday, i);
      return { dia, date, dateStr: toDateStr(date) };
    });
  }, [weekOffset]);
  const fmtDM = (d: Date) => d.toLocaleDateString('es', { day: 'numeric', month: 'short' });

  // ── Abrir modal para agregar (opcionalmente con hora de inicio pre-cargada
  //    al hacer clic en un espacio del calendario) ──
  const openAdd = (dia: number, desdeHora?: string) => {
    if (desdeHora) {
      const finMin = Math.min(hhToMin(desdeHora) + 60, 23 * 60 + 59);
      const fin = `${String(Math.floor(finMin / 60)).padStart(2, '0')}:${String(finMin % 60).padStart(2, '0')}`;
      setForm({ ...EMPTY_FORM, horaInicio: desdeHora, horaFin: fin });
    } else {
      setForm({ ...EMPTY_FORM });
    }
    setModal({ dia });
  };

  // ── Abrir modal para editar ──
  const openEdit = (regla: ReglaHorario) => {
    setForm({
      horaInicio: regla.horaInicio,
      horaFin: regla.horaFin,
      vigenciaDesde: regla.vigenciaDesde.toDate().toISOString().slice(0, 10),
      vigenciaHasta: regla.vigenciaHasta
        ? regla.vigenciaHasta.toDate().toISOString().slice(0, 10)
        : '',
    });
    setModal({ dia: regla.diaSemana, regla });
  };

  const closeModal = () => {
    setModal(null);
    setForm({ ...EMPTY_FORM });
  };

  // ── Guardar (crear o actualizar) ──
  // Intenta usar la CF dedicada de actualización; si no existe (aún no
  // desplegada) hace fallback a delete + create atómico.
  const handleSave = async () => {
    if (!modal) return;
    setSubmitting(true);
    try {
      if (modal.regla) {
        if (esMedico) {
          // No hay CF de actualización para el horario del médico → borrar y recrear.
          await callEliminarHorarioMedico({ salaId, medicoId, reglaId: modal.regla.id });
          await callCrearHorarioMedico({
            salaId, medicoId,
            diaSemana: modal.dia,
            horaInicio: form.horaInicio,
            horaFin: form.horaFin,
            vigenciaDesde: form.vigenciaDesde,
            vigenciaHasta: form.vigenciaHasta || null,
          });
        } else {
          // Editar — intentar CF dedicada, fallback a delete+create
          try {
            await callActualizarRegla({
              salaId,
              reglaId: modal.regla.id,
              horaInicio: form.horaInicio,
              horaFin: form.horaFin,
              vigenciaDesde: form.vigenciaDesde,
              vigenciaHasta: form.vigenciaHasta || null,
            });
          } catch (updateErr: unknown) {
            const code =
              updateErr && typeof updateErr === 'object' && 'code' in updateErr
                ? String((updateErr as { code: unknown }).code)
                : '';
            // "not-found" en functions = CF no desplegada aún
            if (
              code === 'functions/not-found' ||
              code === 'functions/internal'
            ) {
              // Fallback: eliminar la regla vieja y crear una nueva
              await callEliminarRegla({ salaId, reglaId: modal.regla.id });
              await callCrearRegla({
                salaId,
                diaSemana: modal.dia,
                horaInicio: form.horaInicio,
                horaFin: form.horaFin,
                vigenciaDesde: form.vigenciaDesde,
                vigenciaHasta: form.vigenciaHasta || null,
              });
            } else {
              throw updateErr;
            }
          }
        }
        addToast('success', `Bloque actualizado en ${DIAS[modal.dia]}`);
      } else {
        // Crear
        if (esMedico) {
          await callCrearHorarioMedico({
            salaId, medicoId,
            diaSemana: modal.dia,
            horaInicio: form.horaInicio,
            horaFin: form.horaFin,
            vigenciaDesde: form.vigenciaDesde,
            vigenciaHasta: form.vigenciaHasta || null,
          });
        } else {
          await callCrearRegla({
            salaId,
            diaSemana: modal.dia,
            horaInicio: form.horaInicio,
            horaFin: form.horaFin,
            vigenciaDesde: form.vigenciaDesde,
            vigenciaHasta: form.vigenciaHasta || null,
          });
        }
        addToast('success', `Bloque agregado a ${DIAS[modal.dia]}`);
      }
      closeModal();
    } catch (err) {
      console.error('[HorariosEditor] save:', err);
      const msg =
        err instanceof Error
          ? err.message
          : typeof err === 'object' && err && 'message' in err
            ? String((err as { message: unknown }).message)
            : 'Error al guardar el bloque horario';
      addToast('error', msg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (reglaId: string): Promise<boolean> => {
    setDeleting(reglaId);
    try {
      if (esMedico) await callEliminarHorarioMedico({ salaId, medicoId, reglaId });
      else await callEliminarRegla({ salaId, reglaId });
      addToast('success', 'Bloque eliminado');
      return true;
    } catch (err) {
      addToast('error', err instanceof Error ? err.message : 'Error al eliminar');
      return false;
    } finally {
      setDeleting(null);
    }
  };

  // Eliminar desde el modal de edición: borra y cierra solo si tuvo éxito.
  const handleDeleteFromModal = async () => {
    if (!modal?.regla) return;
    const ok = await handleDelete(modal.regla.id);
    if (ok) closeModal();
  };

  const fmtVigencia = (r: ReglaHorario): string => {
    const desde = r.vigenciaDesde.toDate().toISOString().slice(0, 10);
    const hasta = r.vigenciaHasta?.toDate().toISOString().slice(0, 10);
    return hasta ? `${desde}  →  ${hasta}` : `Desde ${desde} (indefinida)`;
  };

  const isEditing = !!modal?.regla;

  // ── Vista semanal (calendario estilo Outlook, con fechas reales) ──
  const renderWeek = () => {
    const bodyH = (endHour - startHour) * HORA_PX;
    const horas = Array.from({ length: endHour - startHour }, (_, i) => startHour + i);
    return (
      <div className={styles.weekWrap}>
        <div className={styles.weekHead}>
          <div />
          {semana.map(({ dia, date, dateStr }) => {
            const fin = dia === 0 || dia === 6;
            const hoy = dateStr === hoyStr;
            return (
              <div
                key={dia}
                className={`${styles.weekHeadCell} ${fin ? styles.weekHeadCellWeekend : ''} ${hoy ? styles.weekHeadCellToday : ''}`}
              >
                <span className={styles.weekHeadDia}>{DIAS[dia].slice(0, 3)}</span>
                <span className={styles.weekHeadFecha}>{date.getDate()}</span>
              </div>
            );
          })}
        </div>
        <div className={styles.weekBody}>
          <div className={styles.weekGutter} style={{ height: bodyH }}>
            {horas.map((h) => (
              <div key={h} className={styles.weekHourCell} style={{ height: HORA_PX }}>
                <span className={styles.weekHourLabel}>{String(h).padStart(2, '0')}:00</span>
              </div>
            ))}
          </div>
          {semana.map(({ dia, dateStr }) => {
            const fin = dia === 0 || dia === 6;
            const hoy = dateStr === hoyStr;
            // En el calendario NO se muestran las reglas VENCIDAS para la fecha
            // de la columna (sí aparecen en la vista de lista).
            const bloques = reglasPorDia[dia].filter((r) => {
              const hastaStr = tsToDateStr(r.vigenciaHasta);
              return !(hastaStr && dateStr > hastaStr);
            });
            const { fullDay, rangos } = suspensionesDeFecha(dateStr, suspensiones);
            const bloqueosDia = bloqueosDeFecha(dia, dateStr, bloqueos);
            return (
              <div
                key={dia}
                className={`${styles.weekDayCol} ${fin ? styles.weekDayColWeekend : ''} ${hoy ? styles.weekDayColToday : ''}`}
                style={{ height: bodyH }}
              >
                {horas.map((h) => (
                  <div
                    key={h}
                    className={styles.weekHourCell}
                    style={{ height: HORA_PX }}
                    onClick={() => openAdd(dia, `${String(h).padStart(2, '0')}:00`)}
                    title={`Agregar bloque · ${DIAS[dia]} ${String(h).padStart(2, '0')}:00`}
                  />
                ))}
                {bloques.length === 0 && fullDay.length === 0 && rangos.length === 0 && bloqueosDia.length === 0 && (
                  <span className={styles.weekEmpty}>—</span>
                )}
                {bloques.map((r) => {
                  const t = turnoDe(dia, r.horaInicio);
                  // Regla aún NO vigente para esta fecha → se marca en gris.
                  const desdeStr = tsToDateStr(r.vigenciaDesde);
                  const futuro = !!(desdeStr && dateStr < desdeStr);
                  const c = futuro
                    ? (isDark ? GRIS_DARK : GRIS_LIGHT)
                    : (isDark ? t.dark : t.light);
                  const top = (hhToMin(r.horaInicio) / 60 - startHour) * HORA_PX;
                  const height = ((hhToMin(r.horaFin) - hhToMin(r.horaInicio)) / 60) * HORA_PX;
                  const titulo = futuro && desdeStr
                    ? `${r.horaInicio}–${r.horaFin} · Aún no vigente — entra en vigencia el ${fmtFechaLarga(desdeStr)} — clic para editar`
                    : `${r.horaInicio}–${r.horaFin} · ${t.label} — clic para editar`;
                  return (
                    <button
                      key={r.id}
                      type="button"
                      className={styles.weekBlock}
                      style={{ top, height: Math.max(height - 2, 18), background: c.fill, borderColor: c.border, color: c.text }}
                      onClick={(e) => { e.stopPropagation(); openEdit(r); }}
                      title={titulo}
                    >
                      <span className={styles.weekBlockTime}>
                        {futuro && <CalendarClock size={10} aria-hidden="true" style={{ marginRight: 3, verticalAlign: '-1px' }} />}
                        {r.horaInicio}–{r.horaFin}
                      </span>
                      {height >= 34 && (
                        <span className={styles.weekBlockTurno}>{futuro ? 'Programado' : t.label}</span>
                      )}
                    </button>
                  );
                })}

                {/* Suspensiones por rango horario — sobre los bloques */}
                {rangos.map((s) => {
                  const top = (hhToMin(s.horaInicio!) / 60 - startHour) * HORA_PX;
                  const height = ((hhToMin(s.horaFin!) - hhToMin(s.horaInicio!)) / 60) * HORA_PX;
                  return (
                    <div
                      key={s.id}
                      className={styles.weekSuspBlock}
                      style={{ top, height: Math.max(height - 2, 16) }}
                      title={`Suspensión ${s.horaInicio}–${s.horaFin}: ${s.motivo}`}
                    >
                      <CalendarOff size={11} aria-hidden="true" />
                      <span className={styles.weekSuspText}>{s.motivo}</span>
                    </div>
                  );
                })}

                {/* Bloqueos recurrentes (almuerzo, reuniones) — sobre los bloques */}
                {bloqueosDia.map((b) => {
                  const top = (hhToMin(b.horaInicio) / 60 - startHour) * HORA_PX;
                  const height = ((hhToMin(b.horaFin) - hhToMin(b.horaInicio)) / 60) * HORA_PX;
                  return (
                    <div
                      key={b.id}
                      className={styles.weekBloqueo}
                      style={{ top, height: Math.max(height - 2, 16) }}
                      title={`Bloqueo ${b.horaInicio}–${b.horaFin}: ${b.motivo}`}
                    >
                      <Lock size={11} aria-hidden="true" />
                      <span className={styles.weekSuspText}>{b.motivo}</span>
                    </div>
                  );
                })}

                {/* Suspensión de día completo / multi-día — cubre la columna */}
                {fullDay.length > 0 && (
                  <div
                    className={styles.weekSuspDay}
                    title={fullDay.map((s) => s.motivo).join(' · ')}
                  >
                    <span className={styles.weekSuspDayChip}>
                      <CalendarOff size={11} aria-hidden="true" /> Suspendido
                    </span>
                    <span className={styles.weekSuspDayMotivo}>
                      {fullDay.map((s) => s.motivo).join(' · ')}
                    </span>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  if (loading) return <p className={styles.empty}>Cargando horarios…</p>;

  return (
    <div className={styles.editor}>
      <div className={styles.toolbar}>
        {view === 'semana' && (
          <div className={styles.weekNav}>
            <button
              type="button"
              className={styles.weekNavBtn}
              onClick={() => setWeekOffset((o) => o - 1)}
              aria-label="Semana anterior"
            >
              <ChevronLeft size={16} />
            </button>
            <span className={styles.weekNavLabel}>
              {fmtDM(semana[0].date)} – {fmtDM(semana[6].date)}
            </span>
            <button
              type="button"
              className={styles.weekNavBtn}
              onClick={() => setWeekOffset((o) => o + 1)}
              aria-label="Semana siguiente"
            >
              <ChevronRight size={16} />
            </button>
            {weekOffset !== 0 && (
              <button
                type="button"
                className={styles.weekNavToday}
                onClick={() => setWeekOffset(0)}
              >
                Hoy
              </button>
            )}
          </div>
        )}
        <div className={styles.viewToggle} role="group" aria-label="Cambiar vista">
          <button
            type="button"
            className={`${styles.viewToggleBtn} ${view === 'semana' ? styles.viewToggleBtnActive : ''}`}
            onClick={() => setView('semana')}
          >
            <CalendarDays size={14} /> Semana
          </button>
          <button
            type="button"
            className={`${styles.viewToggleBtn} ${view === 'lista' ? styles.viewToggleBtnActive : ''}`}
            onClick={() => setView('lista')}
          >
            <List size={14} /> Lista
          </button>
        </div>
      </div>

      {/* ── Vista semanal (calendario) ── */}
      {view === 'semana' && (
        <>
          <div className={styles.weekLegend}>
            <span><span className={styles.legendDot} style={{ background: T_MANANA.accent }} /> Mañana</span>
            <span><span className={styles.legendDot} style={{ background: T_TARDE.accent }} /> Tarde</span>
            <span><span className={styles.legendDot} style={{ background: T_NOCHE.accent }} /> Noche</span>
            <span><span className={styles.legendDot} style={{ background: T_FINDE.accent }} /> Fin de semana</span>
            {!esMedico && <span><span className={styles.legendDotSusp} /> Suspensión</span>}
            {!esMedico && <span><span className={styles.legendDotBloqueo} /> Bloqueo</span>}
            <span className={styles.legendHint}>Clic en un bloque para editar · en un espacio vacío para agregar</span>
          </div>
          {renderWeek()}
        </>
      )}

      {/* ── Vista de lista (días en acordeón) ── */}
      {view === 'lista' && (
      <div className={styles.dayList}>
        {DIAS.map((nombreDia, dia) => {
          const bloques = reglasPorDia[dia];
          const isExpanded = expandedDays.has(dia);
          const isWeekend = dia === 0 || dia === 6;

          return (
            <div
              key={dia}
              className={`${styles.dayRow} ${isWeekend ? styles.dayRowWeekend : ''}`}
            >
              {/* Day header row */}
              <div className={styles.dayHeader}>
                <button
                  type="button"
                  className={styles.dayToggle}
                  onClick={() => toggleDay(dia)}
                  aria-expanded={isExpanded}
                >
                  {isExpanded ? (
                    <ChevronDown size={16} />
                  ) : (
                    <ChevronRight size={16} />
                  )}
                  <span className={styles.dayName}>{nombreDia}</span>
                  <span className={styles.dayCount}>
                    {bloques.length === 0
                      ? 'Sin atención'
                      : `${bloques.length} bloque${bloques.length > 1 ? 's' : ''}`}
                  </span>
                </button>

                <button
                  type="button"
                  className={styles.addBtn}
                  onClick={() => openAdd(dia)}
                  title={`Agregar bloque a ${nombreDia}`}
                >
                  <Plus size={14} />
                  <span className={styles.addBtnLabel}>Agregar</span>
                </button>
              </div>

              {/* Blocks list — shown when expanded */}
              {isExpanded && (
                <div className={styles.blocksContainer}>
                  {bloques.length === 0 ? (
                    <div className={styles.emptyDay}>
                      No hay bloques horarios configurados para este día.
                      <button
                        type="button"
                        className={styles.emptyDayBtn}
                        onClick={() => openAdd(dia)}
                      >
                        <Plus size={12} /> Agregar bloque
                      </button>
                    </div>
                  ) : (
                    <div className={styles.blocksGrid}>
                      {bloques.map((r) => (
                        <div key={r.id} className={styles.blockCard}>
                          <div className={styles.blockTime}>
                            <Clock size={14} className={styles.blockTimeIcon} />
                            <span className={styles.blockTimeText}>
                              {r.horaInicio} — {r.horaFin}
                            </span>
                          </div>
                          <div className={styles.blockValidity}>
                            <CalendarRange
                              size={12}
                              className={styles.blockValidityIcon}
                            />
                            <span>{fmtVigencia(r)}</span>
                          </div>
                          <div className={styles.blockActions}>
                            <button
                              type="button"
                              className={styles.editBtn}
                              onClick={() => openEdit(r)}
                              title="Editar bloque"
                            >
                              <Pencil size={13} />
                            </button>
                            <button
                              type="button"
                              className={styles.delBtn}
                              onClick={() => handleDelete(r.id)}
                              disabled={deleting === r.id}
                              title="Eliminar bloque"
                            >
                              <Trash2 size={13} />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
      )}

      {/* ── Modal para agregar / editar ── */}
      {modal !== null && (
        <div className={styles.modalBackdrop} onClick={closeModal}>
          <div
            className={styles.modal}
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
          >
            <h4 className={styles.modalTitle}>
              {isEditing ? 'Editar bloque' : 'Agregar bloque'} — {DIAS[modal.dia]}
            </h4>

            <div className={styles.modalSection}>
              <span className={styles.modalSectionLabel}>Horario</span>
              <div className={styles.modalRow}>
                <Input
                  label="Hora inicio *"
                  type="time"
                  value={form.horaInicio}
                  onChange={(e) =>
                    setForm({ ...form, horaInicio: e.target.value })
                  }
                  required
                />
                <Input
                  label="Hora fin *"
                  type="time"
                  value={form.horaFin}
                  onChange={(e) =>
                    setForm({ ...form, horaFin: e.target.value })
                  }
                  required
                />
              </div>
            </div>

            <div className={styles.modalSection}>
              <span className={styles.modalSectionLabel}>Vigencia</span>
              <div className={styles.modalRow}>
                <Input
                  label="Desde *"
                  type="date"
                  value={form.vigenciaDesde}
                  onChange={(e) =>
                    setForm({ ...form, vigenciaDesde: e.target.value })
                  }
                  required
                />
                <Input
                  label="Hasta (vacío = indefinida)"
                  type="date"
                  value={form.vigenciaHasta}
                  onChange={(e) =>
                    setForm({ ...form, vigenciaHasta: e.target.value })
                  }
                />
              </div>
            </div>

            <div className={styles.modalActions}>
              {isEditing && (
                <button
                  type="button"
                  className={styles.btnDangerModal}
                  onClick={handleDeleteFromModal}
                  disabled={deleting === modal?.regla?.id || submitting}
                >
                  <Trash2 size={14} />
                  {deleting === modal?.regla?.id ? 'Eliminando…' : 'Eliminar'}
                </button>
              )}
              <button
                type="button"
                className={styles.btnSec}
                onClick={closeModal}
              >
                Cancelar
              </button>
              <button
                type="button"
                className={styles.btnPri}
                onClick={handleSave}
                disabled={submitting}
              >
                {submitting
                  ? 'Guardando…'
                  : isEditing
                    ? 'Guardar cambios'
                    : 'Agregar bloque'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
